/**
 * Pure-data English catalogue for namespace 'respack' (W1-08 formats-legacy-respack; imports only
 * core/util/types): validation errors and warnings of `loadRespack` (Phira resource packs). Errors mean Phira
 * would refuse the pack; warnings describe how Phira will show it. Params are named in braces.
 */

import type { MessageCatalog } from '../../core/util/types';

const EXPECTED =
  '{expected, select, text {text} wholeNumber {a whole number} number {a number} bool {true or false} pair {a pair [a, b] of whole numbers} other {another kind of value}}';

const catalog: MessageCatalog = {
  ns: 'respack',
  locale: 'en',
  messages: {
    // ---- info.yml ----
    'error.missingInfo': 'The pack has no info.yml at its top level.',
    'error.infoInFolder':
      'info.yml is inside the folder "{folder}". Phira only reads it from the top level: zip the files themselves, not the folder.',
    'error.infoNotUtf8': 'info.yml is not valid UTF-8 text.',
    'error.yaml': 'info.yml is not valid YAML (line {line}, column {col}).',
    'error.duplicateKey': 'info.yml sets the same field twice (line {line}).',
    'error.notMapping': 'info.yml must be a list of "field: value" lines.',
    'error.missingField': 'info.yml is missing the required field "{field}".',
    'error.fieldType': `The field "{field}" in info.yml must be ${EXPECTED}.`,
    'error.quotedValue': 'The field "{field}" in info.yml must not be in quotes: Phira reads quoted values as text.',
    'error.outOfRange': 'The field "{field}" in info.yml must be between 0 and {max}.',
    'error.nameEmpty': 'The pack name is empty.',
    'error.nameTooLong': 'The pack name is {bytes} bytes long; Phira allows at most {max}.',
    'error.descriptionTooLong': 'The description is {bytes} bytes long; Phira allows at most {max}.',
    'error.hitFxGrid': 'The hit effect grid hitFx [{cols}, {rows}] must have between 1 and {max} frames.',

    // ---- Files ----
    'error.missingImage': 'The pack has no {file}.',
    'error.imageUndecodable': '{file} is not a PNG, JPEG, GIF, BMP or WebP image that Phira can read.',
    'error.holdAtlas':
      'The hold slices of {file} ({tail} px tail + {head} px head) leave no room for the body: the image is only {height} px high.',
    'error.soundUndecodable':
      '{file} is not an Ogg Vorbis, WAV, MP3 or FLAC sound that Phira can play. Phira uses the first of .ogg, .wav and .mp3 that exists.',

    // ---- Warnings ----
    'warning.holdSoundIgnored': '{file} is ignored: Phira plays the click sound when a hold starts.',
    'warning.hitFxUneven':
      'hit_fx.png ({width} x {height} px) does not divide evenly into the hitFx grid [{cols}, {rows}], so frames are cut unevenly.',
    'warning.colorAlphaZero':
      '{field} has an alpha of 00. Phira cannot tell it from a colour without alpha and draws it fully opaque.',
    'warning.unusedFiles':
      '{count, plural, one {# file is} other {# files are}} not used by Phira (for example "{first}").',
  },
};

export default catalog;
